/**In this component handling tab changes and displaying public announcements */

import React, { useState, useEffect, useRef } from "react";
import { Search } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

const PublicAnnouncements = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [showAudioPlayer, setShowAudioPlayer] = useState(false);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [showDocViewer, setShowDocViewer] = useState(false);
  const [currentDoc, setCurrentDoc] = useState(null);
  const [categories, setCategories] = useState([
    "All",
    "Active",
    "Pause",
    "Inactive",
  ]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [typeCategories, setTypeCategories] = useState([
    "All",
    "Document",
    "Audio",
  ]);
  const [selectedType, setSelectedType] = useState("All");
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const progressBarRef = useRef(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [inactiveItem, setInactiveItem] = useState(null);
  const location = useLocation();
  const [activePage, setActivePage] = useState(
    location.pathname.includes("schedule") ? "schedule" : "announcements"
  );

  //To handle tab changing
  useEffect(() => {
    if (location.pathname.includes("schedule")) {
      setActivePage("schedule");
    } else {
      setActivePage("announcements");
    }
  }, [location.pathname]);

  // Audio files - these would be your static audio files
  const audioFiles = {
    atm: "/audio/atm-announcement.mp3",
    banking: "/audio/banking-services.mp3",
    shifting: "/audio/shifting-notice.mp3",
    unclaimed: "/audio/unclaimed-deposits.mp3",
    revision: "/audio/revision-charges.mp3",
    validity: "/audio/validity-period.mp3",
    closure: "/audio/closure-accounts.mp3",
  };

  //Static data to display existing announcements
  const announcements = [
    {
      id: 1,
      title: "ATM: PIN Supply Debit Card/Credentials",
      date: "19-10-2024",
      type: "audio",
      status: "Active",
      audioSrc: audioFiles.atm,
      playedAt: "All Branches",
      frequency: "35 Times",
    },
    {
      id: 2,
      title: "Consumer Education (Important Information)",
      date: "19-10-2024",
      type: "document",
      status: "Inactive",
      docSrc: "/sample-doc.pdf",
      playedAt: "All Branches",
      frequency: "20 Times",
    },
    {
      id: 3,
      title:
        "Continuity Of Banking Services During Calamity, Strike Or Disruptions",
      date: "19-10-2024",
      type: "audio",
      status: "Active",
      audioSrc: audioFiles.banking,
      playedAt: "All Branches",
      frequency: "20 Times",
    },
    {
      id: 4,
      title: "Shifting notice",
      date: "19-10-2024",
      type: "audio",
      status: "Active",
      audioSrc: audioFiles.shifting,
      playedAt: "All Branches",
      frequency: "30 Times",
    },
    {
      id: 5,
      title: "Benchmark Rate (LIBOR) Transition Dated 12-10-2024",
      date: "19-10-2024",
      type: "document",
      status: "Pause",
      docSrc: "/sample-doc.pdf",
      playedAt: "All Branches",
      frequency: "20 Times",
    },
    {
      id: 6,
      title: "Unclaimed Deposits & Inoperative Account Policy",
      date: "19-10-2024",
      type: "audio",
      status: "Active",
      audioSrc: audioFiles.unclaimed,
      playedAt: "All Branches",
      frequency: "30 Times",
    },
    {
      id: 7,
      title: "Revision in non-credit related Service Charges",
      date: "19-10-2024",
      type: "audio",
      status: "Inactive",
      audioSrc: audioFiles.revision,
      playedAt: "All Branches",
      frequency: "34 Times",
    },
    {
      id: 8,
      title: "Amendment To Service Charges - Auto Handling Charges",
      date: "19-10-2024",
      type: "document",
      status: "Inactive",
      docSrc: "/sample-doc.pdf",
      playedAt: "All Branches",
      frequency: "20 Times",
    },
    {
      id: 9,
      title: "Validity Period: 13-10-2024 - 19-10-2024",
      date: "19-10-2024",
      type: "audio",
      status: "Inactive",
      audioSrc: audioFiles.validity,
      playedAt: "All Branches",
      frequency: "30 Times",
    },
    {
      id: 10,
      title: "Closure of Zero Balance Inoperative Accounts",
      date: "19-10-2024",
      type: "audio",
      status: "Pause",
      audioSrc: audioFiles.closure,
      playedAt: "All Branches",
      frequency: "10 Times",
    },
    {
      id: 11,
      title: "Timeline for Disposal of Credit Proposals (Multi)",
      date: "19-10-2024",
      type: "document",
      status: "Pause",
      docSrc: "/sample-doc.pdf",
      playedAt: "All Branches",
      frequency: "20 Times",
    },
    {
      id: 12,
      title: "The Service Charges Are Being Revised To Be Applicable",
      date: "19-10-2024",
      type: "document",
      status: "Inactive",
      docSrc: "/sample-doc.pdf",
      playedAt: "All Branches",
      frequency: "20 Times",
    },
  ];

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Effect to handle audio playback
  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.play().catch((error) => {
          console.error("Error playing audio:", error);
          setIsPlaying(false);
        });
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying]);

  // Effect to handle audio loading when current audio changes
  useEffect(() => {
    if (currentAudio && audioRef.current) {
      // Reset the audio player
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
      setDuration(0);
      setIsPlaying(false);
    }
  }, [currentAudio]);
  console.log(setCategories, setTypeCategories, showStatusModal, inactiveItem);
  // Effect to update audio time
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
    };
  }, [currentAudio]);

  //handling audio files
  const handleAudioClick = (announcement) => {
    if (announcement.type === "audio") {
      setCurrentAudio(announcement);
      setShowAudioPlayer(true);
    }
  };

  //handling doc preview
  const handleDocClick = (announcement) => {
    if (announcement.type === "document") {
      setCurrentDoc(announcement);
      setShowDocViewer(true);
    }
  };

  //handling audio and doc active inactive states while displaying the content
  const handleCardClick = (announcement) => {
    if (announcement.status === "Inactive") {
      setInactiveItem(announcement);
      setShowStatusModal(true);
      return;
    }
    if (announcement.type === "audio") {
      handleAudioClick(announcement);
    } else if (announcement.type === "document") {
      handleDocClick(announcement);
    }
  };

  const closeAudioPlayer = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setIsPlaying(false);
    setShowAudioPlayer(false);
    setCurrentAudio(null);
  };

  const closeDocViewer = () => {
    setShowDocViewer(false);
    setCurrentDoc(null);
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setSelectedCategory(tab);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  // Function to handle audio end
  const handleAudioEnd = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  // Format time in MM:SS format
  const formatTime = (time) => {
    if (isNaN(time)) return "00:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  // Handle progress bar click to seek
  const handleProgressBarClick = (e) => {
    if (!progressBarRef.current || !audioRef.current) return;

    const progressBar = progressBarRef.current;
    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const progressBarWidth = rect.width;

    // Calculate percentage
    const percentage = clickX / progressBarWidth;

    // Calculate new time
    const newTime = percentage * duration;

    // Set audio time
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Filter announcements based on active tab, selected type, and search query
  const filteredAnnouncements = announcements.filter((announcement) => {
    const matchesTab =
      selectedCategory === "All" || announcement.status === selectedCategory;
    const matchesType =
      selectedType === "All" ||
      announcement.type === selectedType.toLowerCase();
    const matchesSearch = announcement.title
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    return matchesTab && matchesType && matchesSearch;
  });

  // Get status badge color
  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case "Active":
        return {
          backgroundColor: "rgba(0, 191, 40, 0.1)",
          color: "#00BF28",
        };
      case "Inactive":
        return {
          backgroundColor: "rgba(233, 14, 36, 0.1)",
          color: "#E95060",
        };
      case "Pause":
        return {
          backgroundColor: "rgba(255, 161, 0, 0.1)",
          color: "#FFA100",
        };
      default:
        return {
          backgroundColor: "rgba(128, 128, 128, 0.1)",
          color: "#808080",
        };
    }
  };

  const toggleCategoryDropdown = () => {
    setShowCategoryDropdown(!showCategoryDropdown);
    if (showMonthDropdown) setShowMonthDropdown(false);
  };

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setShowCategoryDropdown(false);
  };

  // Calculate progress percentage for the audio progress bar
  const calculateProgress = () => {
    if (!duration) return 0;
    return (currentTime / duration) * 100;
  };

  // Sound Icon SVG Component (replacement for Audio Icon)
  const SoundIcon = () => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11 5L6 9H2V15H6L11 19V5Z"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15.54 8.46C16.4774 9.39764 17.004 10.6692 17.004 11.995C17.004 13.3208 16.4774 14.5924 15.54 15.53"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19.07 5.93C20.9447 7.80528 21.9979 10.3447 21.9979 13C21.9979 15.6553 20.9447 18.1947 19.07 20.07"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  // Play/Pause Icon Component
  const PlayPauseIcon = ({ isPlaying }) =>
    isPlaying ? (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="6" y="4" width="4" height="16" rx="1" fill="#2596be" />
        <rect x="14" y="4" width="4" height="16" rx="1" fill="#2596be" />
      </svg>
    ) : (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M6 4L19 12L6 20V4Z" fill="#2596be" />
      </svg>
    );

  // Document Icon SVG Component
  const DocIcon = () => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9 17H15M9 13H15M9 9H15M5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21Z"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  //navigation to create annpuncement
  const navToCreateAnnouncement = () => {
    navigate("/information-center/create-announcement");
  };

  return (
    <div
      className="main-container"
      style={{ backgroundColor: "white", padding: "0px" }}
    >
      {/* Header Tabs - On white background */}
      <div
        className="header-tabs"
        style={{
          display: "flex",
          marginBottom: "-5px",
          marginLeft: "-12px",
          marginRight: "-12px",
          backgroundColor: "white",
          padding: "8px 16px",
          borderRadius: "8px",
        }}
      >
        <div
          className="tab"
          onClick={() => {
            setActivePage("announcements");
            navigate("../public-announcements");
          }}
          style={{
            padding: "10px 20px",
            backgroundColor:
              activePage === "announcements" ? "#e4f0f4" : "transparent",
            borderRadius: "8px",
            color: activePage === "announcements" ? "#1a7a73" : "#809fb8",
            fontWeight: "600",
            marginRight: "10px",
            fontSize: "15px",
            cursor: "pointer",
          }}
        >
          Public Announcements
        </div>
        <div
          className="tab"
          onClick={() => {
            setActivePage("schedule");
            navigate("/information-center/schedule-announcement");
          }}
          style={{
            padding: "10px 20px",
            backgroundColor:
              activePage === "schedule" ? "#e4f0f4" : "transparent",
            borderRadius: "8px",
            color: activePage === "schedule" ? "#1a7a73" : "#809fb8",
            fontWeight: "600",
            fontSize: "15px",
            cursor: "pointer",
          }}
        >
          Schedule
        </div>
        <div style={{ marginLeft: "auto" }}>
          <button
            onClick={navToCreateAnnouncement}
            style={{
              backgroundColor: "#2596be",
              color: "white",
              border: "none",
              borderRadius: "4px",
              padding: "10px 15px",
              marginTop: "5px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            Create an Announcement
          </button>
        </div>
      </div>

      {/* Content area with gray background and rounded corners */}
      <div
        className="content-container"
        style={{
          backgroundColor: "#F9F9F9",
          borderRadius: "12px",
          padding: "10px",
        }}
      >
        {/* Filter Tabs and Search */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "20px",
            padding: "12px 16px",
            marginLeft: "-10px",
          }}
        >
          <div
            style={{
              display: "flex",
              backgroundColor: "white",
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              overflow: "hidden",
            }}
          >
            {categories.map((category, index) => (
              <>
                <button
                  key={category}
                  onClick={() => handleTabClick(category)}
                  style={{
                    padding: "5px 10px",
                    border: "none",
                    backgroundColor:
                      activeTab === category ? "#2596be" : "white",
                    color: activeTab === category ? "white" : "#666",
                    cursor: "pointer",
                    fontWeight: "500",
                    fontSize: "12px",
                    margin: "0",
                  }}
                >
                  {category}
                </button>
                {index < categories.length - 1 && (
                  <div
                    style={{
                      width: "1px",
                      margin: "3px",
                      backgroundColor: "#e0e0e0",
                    }}
                  />
                )}
              </>
            ))}
          </div>

          {/* Type Categories Dropdown - MODIFIED */}
          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ position: "relative" }}>
              <button
                onClick={toggleCategoryDropdown}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "4px 12px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  backgroundColor: "white",
                  cursor: "pointer",
                  fontSize: "14px",
                  color: "grey",
                }}
              >
                {selectedType === "All" ? "All Categories" : selectedType}{" "}
                <span style={{ marginLeft: "5px", color: "grey" }}>▼</span>
              </button>
              {showCategoryDropdown && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    width: "150px",
                    backgroundColor: "white",
                    border: "1px solid #ddd",
                    borderRadius: "5px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    zIndex: 10,
                  }}
                >
                  {typeCategories.map((type) => (
                    <div
                      key={type}
                      onClick={() => handleTypeSelect(type)}
                      style={{
                        padding: "8px 12px",
                        cursor: "pointer",
                        backgroundColor:
                          selectedType === type ? "#f0f0f0" : "transparent",
                      }}
                    >
                      {type}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Search Bar */}
          <div
            style={{
              width: windowWidth < 768 ? "200px" : "200px", // Reduced width values
              position: "relative",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                border: "1px solid #ddd",
                borderRadius: "5px",
                padding: "0 10px",
                backgroundColor: "white",
                width: "100%", // Ensure the inner div takes full width of container
                boxSizing: "border-box", // Include padding in width calculation
              }}
            >
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={handleSearch}
                style={{
                  flex: 1,
                  border: "none",
                  outline: "none",
                  padding: "4px 4px",
                  fontSize: "14px",
                  minWidth: "0", // Allow the input to shrink
                }}
              />
              <Search size={18} color="#666" />
            </div>
          </div>
        </div>

        {/* Announcements Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: "20px",
            marginTop: "-10px",
          }}
        >
          {filteredAnnouncements.map((announcement) => (
            <div
              key={announcement.id}
              style={{
                borderRadius: "12px",
                overflow: "hidden",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                backgroundColor: "#fff",
                position: "relative",
                cursor: "pointer",
                transition: "transform 0.2s, box-shadow 0.2s",
                height: "100px",
              }}
              onClick={() => handleCardClick(announcement)}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 6px 12px rgba(0, 0, 0, 0.1)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 2px 8px rgba(0, 0, 0, 0.05)";
              }}
            >
              {/* Header */}
              <div
                style={{
                  display: "flex",
                  padding: "16px",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    backgroundColor: "#2596be",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: "14px",
                  }}
                >
                  {announcement.type === "audio" ? <SoundIcon /> : <DocIcon />}
                </div>

                <div style={{ flex: 1 }}>
                  <div
                    style={{ fontWeight: 400, fontSize: "12px", color: "#333" }}
                  >
                    {announcement.title}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#aaa",
                      marginTop: "4px",
                    }}
                  >
                    {announcement.date}
                  </div>
                </div>

                <div
                  style={{
                    fontSize: "12px",
                    backgroundColor: "#ffeae6",
                    borderRadius: "12px",
                    padding: "4px 10px",
                    fontWeight: 500,
                    ...getStatusBadgeStyle(announcement.status),
                  }}
                >
                  {announcement.status}
                </div>
              </div>

              {/* Footer */}
              {/* <div
                style={{
                  padding: "10px 16px",
                  fontSize: "9px",
                  color: "#666",
                }}
              >
                Played at: {announcement.playedAt} &nbsp; | &nbsp; Frequency: {announcement.frequency}
              </div> */}
            </div>
          ))}
        </div>
      </div>

      {/* Audio Player Modal */}
      {showAudioPlayer && currentAudio && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "20px",
              width: "90%",
              maxWidth: "500px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "20px",
              }}
            >
              <h6>{currentAudio.title}</h6>
              <button
                onClick={closeAudioPlayer}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "12px",
                  cursor: "pointer",
                }}
              >
                ✕
              </button>
            </div>

            {/* Hidden audio element - this is what actually plays the audio */}
            <audio
              ref={audioRef}
              src={currentAudio.audioSrc}
              onEnded={handleAudioEnd}
              preload="auto"
            />

            {/* Custom audio player UI */}
            <div
              style={{
                marginBottom: "20px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <button
                onClick={togglePlay}
                style={{
                  backgroundColor: "#f0f0f0",
                  border: "none",
                  borderRadius: "50%",
                  width: "60px",
                  height: "60px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  marginBottom: "15px",
                }}
              >
                <PlayPauseIcon isPlaying={isPlaying} />
              </button>

              {/* Progress bar - Updated to be clickable and show actual progress */}
              <div
                ref={progressBarRef}
                onClick={handleProgressBarClick}
                style={{
                  width: "100%",
                  height: "4px",
                  backgroundColor: "#e0e0e0",
                  borderRadius: "2px",
                  position: "relative",
                  cursor: "pointer",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    height: "100%",
                    width: `${calculateProgress()}%`, // Dynamic width based on current time
                    backgroundColor: "#2596be",
                    borderRadius: "2px",
                  }}
                ></div>
              </div>

              {/* Time display - Updated to show actual time */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                  marginTop: "8px",
                  fontSize: "12px",
                  color: "#666",
                }}
              >
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            <div style={{ marginTop: "20px", fontSize: "12px", color: "#666" }}>
              <p>Date: {currentAudio.date}</p>
              {/* <p>Played at: {currentAudio.playedAt} &nbsp; | &nbsp; Frequency: {currentAudio.frequency}</p> */}
            </div>
          </div>
        </div>
      )}

      {/* Document Viewer Modal */}
      {showDocViewer && currentDoc && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "20px",
              width: "90%",
              maxWidth: "800px",
              height: "80vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "20px",
              }}
            >
              <h6>{currentDoc.title}</h6>
              <button
                onClick={closeDocViewer}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "12px",
                  cursor: "pointer",
                }}
              >
                ✕
              </button>
            </div>
            <div
              style={{
                flex: 1,
                border: "1px solid #e0e0e0",
                padding: "10px",
                overflow: "auto",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <svg
                  width="64"
                  height="64"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z"
                    stroke="#999"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M14 2V8H20M9 13H15M9 17H15M9 9H10"
                    stroke="#999"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <p style={{ marginTop: "15px", color: "#666" }}>
                  No document available
                </p>
              </div>
            </div>
            <div style={{ marginTop: "20px", fontSize: "12px", color: "#666" }}>
              <p>Date: {currentDoc.date}</p>
              {/* <p>Played at: {currentDoc.playedAt} &nbsp; | &nbsp; Frequency: {currentDoc.frequency}</p> */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicAnnouncements;
